import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import {
  AuthService,
  ChatService,
  MemberService,
  MenuDataService,
  SignalRService,
} from 'shared';
import { RouterOutlet } from '@angular/router';
import { mergeMap } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [RouterOutlet]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private PrimeNG = inject(PrimeNG);
  private menuDataService = inject(MenuDataService);
  private memberService = inject(MemberService);
  private authService = inject(AuthService);
  private chatService = inject(ChatService);
  private signalRService = inject(SignalRService);

  model: any = [
    {
      label: '',
      icon: 'pi pi-fw pi-sitemap',
      items: [
        {
          label: 'Το προφίλ μου',
          icon: 'pi pi-fw pi-home',
          routerLink: [''],
        },
        {
          label: 'Το ημερολόγιό μου​',
          icon: 'pi pi-fw pi-calendar',
          routerLink: ['/calendar'],
        },
        {
          label: 'Κλείσε ραντεβού',
          icon: 'pi pi-fw pi-calendar-plus',
          items: [
            {
              label: 'Ιατροί​',
              icon: 'pi pi-fw pi-user',
              routerLink: ['/visit-form'],
            },
            {
              label: 'Διαγνωστικά κέντρα​',
              icon: 'pi pi-fw pi-search-plus',
              routerLink: ['/book-exams'],
            },
            {
              label: 'Νοσοκομεία​',
              icon: 'pi pi-fw pi-home',
              routerLink: ['/visit-hospital'],
            },
          ],
        },
        {
          label: 'Τα συμβόλαιά μου​',
          icon: 'pi pi-fw pi-bookmark',
          routerLink: ['/insurance-contracts'],
        },
        {
          label: 'Οι προεγκρίσεις μου (εκτός δικτύου)',
          icon: 'pi pi-fw pi-external-link',
          // routerLink: [''],
        },
        {
          label: 'Ο ασφαλιστής μου',
          icon: 'pi pi-fw pi-user-edit',
          routerLink: ['/insurer'],
        },
        // {
        //   label: 'Οι αποζημιώσεις μου',
        //   icon: 'pi pi-fw pi-dollar',
        //   routerLink: ['/compensations'],
        // },
         {
           label: 'Οι απαιτήσεις​ μου',
           icon: 'pi pi-fw pi-money-bill',
           routerLink: ['/claims'],
         },
        {
          label: 'Ο φάκελος υγείας μου',
          icon: 'pi pi-fw pi-briefcase',
          items: [
            {
              label: 'Οι διαγνώσεις μου',
              icon: 'pi pi-fw pi-sign-in',
              routerLink: ['/medical_record/diagnosis'],
            },
            {
              label: 'Οι επισκέψεις μου',
              icon: 'pi pi-fw pi-times-circle',
              routerLink: ['/medical_record/doctor-visits'],
            },
            {
              label: 'Οι ιατρικές πράξεις μου​',
              icon: 'pi pi-fw pi-lock',
              routerLink: ['/medical_record/medical-actions'],
            },
            {
              label: 'Οι νοσηλείες μου​',
              icon: 'pi pi-fw pi-heart',
              routerLink: ['/medical_record/hospitalizations'],
            },
            {
              label: 'Τα φάρμακα μου​',
              icon: 'pi pi-fw pi-calendar-plus',
              routerLink: ['/medical_record/medicines'],
            },
            // {
            //   label: 'Γνωματεύσεις​',
            //   icon: 'pi pi-fw pi-file',
            //   routerLink: ['/medical_record/diagnoses'],
            // },
            // },
            // {
            //   label: 'Ακτινοθεραπείες​',
            //   icon: 'pi pi-fw pi-image',
            //   routerLink: ['/medical_record/radiotherapies'],
            // },

            {
              label: 'Αιτήματα Πρόσβασης​',
              icon: 'pi pi-fw pi-key ',
              routerLink: ['/medical_record/requests'],
            },
          ],
        },
        {
          label: 'Οι συνταγές/Τα παραπεμπτικά μου​',
          icon: 'pi pi-fw pi-file-edit',
          routerLink: ['/prescriptions-references'],
        },
        {
          label: 'Προσωπικός Βοηθός​',
          icon: 'pi pi-fw pi-android',
          routerLink: ['/insured-assistant'],
        },
      ],
    },
  ];

  // ── WCAG: PrimeNG dropdown label transparent-colour patch ─────────────────
  // PrimeNG's float-label animation sets `style="color: rgba(r,g,b,0)"` (alpha=0)
  // directly on the DOM element via JS/Renderer2.  CSS !important overrides the
  // *computed* colour, but WCAG scanners read element.style.color (the raw inline
  // attribute) and see alpha=0 / contrast=1:1.  A MutationObserver that immediately
  // replaces any zero-alpha inline colour with the opaque accessible value (#595959)
  // fixes this at the source before any scanner can capture it.
  private _dropdownLabelObserver: MutationObserver | null = null;

  ngAfterViewInit(): void {
    const ACCESSIBLE_COLOR = '#595959'; // 7.0:1 on white – WCAG AAA
    const fixElement = (el: HTMLElement) => {
      const c = el.style.color;
      if (!c) return;
      // Detect any colour with alpha = 0  (e.g. rgba(89,89,89,0))
      const alphaZero = /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0\s*\)/.test(c);
      if (alphaZero) {
        el.style.color = ACCESSIBLE_COLOR;
      }
    };

    // ── WCAG: label filter-search inputs that PrimeNG renders without aria-label ──
    // When a p-dropdown has [filter]="true", PrimeNG injects an <input class="p-dropdown-filter">
    // into the overlay panel.  It has no associated <label> and no aria-label, so WAVE
    // reports it as "Missing form label".  We derive a meaningful label from the nearest
    // parent combobox element's own aria-label.
    const fixFilterInput = (input: HTMLElement) => {
      if (input.getAttribute('aria-label')) return; // already labelled
      // Walk up to find the dropdown trigger which carries the user-facing aria-label
      const panel = input.closest('.p-dropdown-panel');
      // The panel id follows the pattern "<dropdownId>_list" → derive "<dropdownId>"
      const panelId = panel?.id ?? '';
      const triggerId = panelId.replace(/_list$/, '');
      const trigger = triggerId
        ? document.getElementById(triggerId)
        : null;
      const triggerLabel = trigger?.getAttribute('aria-label') ?? '';
      const label = triggerLabel
        ? `Αναζήτηση: ${triggerLabel}`
        : 'Αναζήτηση';
      input.setAttribute('aria-label', label);
    };

    // Initial pass – fix any already-rendered labels
    document
      .querySelectorAll<HTMLElement>(
        '.p-dropdown-label, .p-placeholder'
      )
      .forEach(fixElement);
    document
      .querySelectorAll<HTMLElement>('.p-dropdown-filter')
      .forEach(fixFilterInput);

    this._dropdownLabelObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // Style attribute changed on an existing node
        if (m.type === 'attributes' && m.attributeName === 'style') {
          fixElement(m.target as HTMLElement);
        }
        // New nodes added (e.g. route change renders new dropdowns or overlay panels)
        if (m.type === 'childList') {
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            const el = node as HTMLElement;
            if (
              el.classList?.contains('p-dropdown-label') ||
              el.classList?.contains('p-placeholder')
            ) {
              fixElement(el);
            }
            el
              .querySelectorAll?.<HTMLElement>(
                '.p-dropdown-label, .p-placeholder'
              )
              .forEach(fixElement);
            // Fix filter search inputs in newly opened overlay panels
            if (el.classList?.contains('p-dropdown-filter')) {
              fixFilterInput(el);
            }
            el
              .querySelectorAll?.<HTMLElement>('.p-dropdown-filter')
              .forEach(fixFilterInput);
          });
        }
      }
    });

    this._dropdownLabelObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true,
    });
  }

  ngOnInit(): void {
    this.PrimeNG.ripple.set(true);
    this.menuDataService.update(this.model);
    this.memberService.appName = 'insured';

    const token = JSON.parse(localStorage.getItem('token')!);
    if (token) {
      this.signalRService.startConnection(token.token);
      this.memberService
        .getEntityByUsername(token.token)
        .pipe(
          mergeMap((user) => {
            return this.authService.fetchUser(user);
          })
        )
        .subscribe(() => {
          throw 'Chat Service Unimplemented';
          // this.chatService.createCurrentSession();
        });
    }
  }
  ngOnDestroy(): void {
    this._dropdownLabelObserver?.disconnect();
    this.signalRService.destroyConnection();
  }
}
